import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service';
import './Register.css';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    department: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return '비밀번호는 8자리 이상이어야 합니다.';
    }

    let complexityCount = 0;
    if (/[A-Z]/.test(password)) complexityCount++;
    if (/[a-z]/.test(password)) complexityCount++;
    if (/[0-9]/.test(password)) complexityCount++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) complexityCount++;

    if (complexityCount < 3) {
      return '비밀번호는 대문자, 소문자, 숫자, 특수문자 중 3가지 이상을 조합해야 합니다.';
    }

    return null;
  };

  const validateEmailDomain = (email: string): boolean => {
    const allowedDomains = ['vntgcorp.com', 'seah.co.kr'];
    const domain = email.split('@')[1];
    return allowedDomains.includes(domain);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 비밀번호 확인
    if (formData.password !== formData.confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    // 비밀번호 복잡도 검증
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    // 이메일 도메인 검증
    if (!validateEmailDomain(formData.email)) {
      setError('vntgcorp.com 또는 seah.co.kr 도메인의 이메일만 가입 가능합니다.');
      return;
    }

    setIsLoading(true);

    try {
      await authService.register({
        name: formData.name,
        company: formData.company,
        department: formData.department,
        email: formData.email,
        password: formData.password,
      });
      alert('회원가입이 완료되었습니다. 로그인해주세요.');
      navigate('/login');
    } catch (err: any) {
      setError(err.response?.data?.message || '회원가입에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-card">
        <h2>회원가입</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">이름</label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="company">회사명</label>
            <input
              id="company"
              name="company"
              type="text"
              value={formData.company}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="department">부서명</label>
            <input
              id="department"
              name="department"
              type="text"
              value={formData.department}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">이메일</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="example@vntgcorp.com"
            />
            <small className="hint">vntgcorp.com 또는 seah.co.kr 도메인만 가능</small>
          </div>
          <div className="form-group">
            <label htmlFor="password">비밀번호</label>
            <input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
            />
            <small className="hint">
              8자리 이상, 대문자/소문자/숫자/특수문자 중 3가지 이상 조합
            </small>
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">비밀번호 확인</label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          <button type="submit" disabled={isLoading} className="submit-button">
            {isLoading ? '가입 중...' : '회원가입'}
          </button>
        </form>
        <div className="login-link">
          이미 계정이 있으신가요? <a href="/login">로그인</a>
        </div>
      </div>
    </div>
  );
};

export default Register;

